import { useCallback, useEffect, useRef, useState } from "react"
import { Button, Input, Tooltip, TooltipContent, TooltipTrigger } from "@ui/shad"
import { cn } from "@ui/utils"
import { Loader2, X } from "lucide-react"

import { BaseInlineInput } from "./base"
import { InlineInputProps } from "./props"
import { useInlineEdit } from "./use-inline-edit"

export function InlineInput<T = string>({
  label,
  LabelIcon,
  hideLabel = false,
  size,
  placeholder = "",
  focusedPlaceholder,
  value: controlledValue,
  defaultValue,
  onChange,
  onSave,
  isSaving = false,
  disabled = false,
  inputClasses,
  tooltipOnOverflow = false,
  validator,
  formatter,
  focusedFormatter,
  parser,
  clearable = true,
  maxLength,
  fixedLabelWidth,
  resetOnInvalid = true,
  resetOnEmpty = false,
  cursorMinPosition,
  cursorMaxOffset,
  onKeyDown: customKeyDown,
  autoComplete,
}: InlineInputProps<T>) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isFocused, setIsFocused] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const { stringValue, setValue, isEmpty, isValid, isSubmitting, handleSubmit, handleKeyDown, handleClear } =
    useInlineEdit({
      value: controlledValue,
      defaultValue,
      onChange,
      onSubmit: onSave,
      validator,
      formatter: isFocused && focusedFormatter ? focusedFormatter : formatter,
      parser,
      ref: inputRef,
      resetOnInvalid,
      resetOnEmpty,
      disableClickOutside: true,
    })

  const constrainCursor = useCallback(() => {
    const input = inputRef.current
    if (!input) return

    const min = cursorMinPosition ?? 0
    const max = input.value.length - (cursorMaxOffset ?? 0)
    const clamp = (v: number) => Math.max(min, Math.min(max, v))

    input.setSelectionRange(clamp(input.selectionStart ?? min), clamp(input.selectionEnd ?? max))
  }, [cursorMinPosition, cursorMaxOffset])

  const measureOverflow = useCallback(() => {
    const input = inputRef.current
    setIsOverflowing(tooltipOnOverflow && !!input && input.scrollWidth > input.clientWidth)
  }, [tooltipOnOverflow])

  useEffect(() => {
    measureOverflow()

    const input = inputRef.current
    if (!tooltipOnOverflow || !input || typeof ResizeObserver === "undefined") return

    const resizeObserver = new ResizeObserver(measureOverflow)
    resizeObserver.observe(input)
    return () => resizeObserver.disconnect()
  }, [measureOverflow, stringValue, tooltipOnOverflow])

  const handleSelect = useCallback(() => {
    if (cursorMinPosition !== undefined || cursorMaxOffset !== undefined) {
      constrainCursor()
    }
  }, [cursorMinPosition, cursorMaxOffset, constrainCursor])

  const handleFocus = useCallback(() => {
    measureOverflow()
    setIsFocused(true)
    setTimeout(() => {
      if (cursorMinPosition !== undefined || cursorMaxOffset !== undefined) {
        constrainCursor()
      }
    }, 0)
  }, [cursorMinPosition, cursorMaxOffset, constrainCursor, measureOverflow])

  return (
    <BaseInlineInput
      label={label}
      LabelIcon={LabelIcon}
      hideLabel={hideLabel}
      size={size}
      fixedLabelWidth={fixedLabelWidth}
    >
      <div className="relative w-full">
        <Tooltip open={tooltipOnOverflow && isOverflowing && (isFocused || isHovering)}>
          <TooltipTrigger asChild>
            <Input
              ref={inputRef}
              type="text"
              autoComplete={autoComplete}
              value={stringValue}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (customKeyDown?.(e, stringValue, setValue)) {
                  e.preventDefault()
                  return
                }
                handleKeyDown(e)
              }}
              onBlur={() => {
                setIsFocused(false)
                handleSubmit()
              }}
              onFocus={handleFocus}
              onSelect={handleSelect}
              onClick={handleSelect}
              onMouseEnter={() => {
                measureOverflow()
                setIsHovering(true)
              }}
              onMouseLeave={() => setIsHovering(false)}
              className={cn(
                "hover:bg-muted focus-visible:border-input h-8 w-full cursor-pointer truncate border-1 border-transparent px-2 shadow-none focus-visible:shadow-sm",
                isEmpty ? "text-input-placeholder" : "",
                !isValid ? "border-destructive" : "",
                clearable && !isEmpty && isFocused && "pr-8",
                typeof inputClasses === "function" ? inputClasses(stringValue) : inputClasses
              )}
              placeholder={isFocused && focusedPlaceholder ? focusedPlaceholder : placeholder}
              disabled={disabled}
              maxLength={maxLength}
            />
          </TooltipTrigger>
          {tooltipOnOverflow && isOverflowing && stringValue && (
            <TooltipContent className="max-w-md break-words">{stringValue}</TooltipContent>
          )}
        </Tooltip>

        {isSaving || isSubmitting ? (
          <Loader2 className="text-icon absolute top-2 right-2 h-4 w-4 animate-spin" />
        ) : (
          clearable &&
          !isEmpty &&
          isFocused && (
            <Button
              variant="ghost"
              size="iconXs"
              onMouseDown={(e) => {
                e.preventDefault()
                handleClear()
              }}
              className="absolute top-1 right-1"
            >
              <X className="text-icon" />
            </Button>
          )
        )}
      </div>
    </BaseInlineInput>
  )
}
